import React from 'react';
import { IconButton, IconButtonProps, Tooltip, SxProps, Theme as MuiTheme } from '@mui/material';
import { useTheme } from '@mui/material/styles';

export type ButtonVariant = 
  | 'primary'      // Primary action button (Save, Create)
  | 'secondary'    // Secondary/back button
  | 'error'        // Delete/destructive action
  | 'neutral'      // Neutral/gray button
  | 'toggle';      // Toggle button with active/inactive states

export type ButtonSize = 'small' | 'medium' | 'large' | 'custom';

export interface ButtonProps extends Omit<IconButtonProps, 'size'> {
  /** Variant determines the color scheme */
  variant?: ButtonVariant;
  
  /** Size of the button */
  size?: ButtonSize;
  
  /** Custom size when size='custom' (e.g., '1rem') */
  customSize?: string;
  
  /** Icon to display (React element) */
  icon: React.ReactElement;
  
  /** Tooltip text */
  tooltip?: string;
  
  /** Whether the button is in an active/selected state (for toggle variant) */
  active?: boolean;
}

/**
 * Reusable Button component based on MUI IconButton
 * Supports multiple variants, sizes, and configurations
 * Uses theme from @my-helpers/common for consistency
 * 
 * @example
 * // Primary save button
 * <Button
 *   variant="primary"
 *   icon={<Save />}
 *   tooltip="Save changes"
 *   onClick={handleSave}
 *   disabled={!isValid}
 * />
 * 
 * @example
 * // Back button
 * <Button
 *   variant="secondary"
 *   icon={<ArrowBack />}
 *   tooltip="Back to list"
 *   onClick={() => navigate('/list')}
 * />
 * 
 * @example
 * // Delete button
 * <Button
 *   variant="error"
 *   icon={<Delete />}
 *   tooltip="Delete item"
 *   onClick={handleDelete}
 * />
 * 
 * @example
 * // Toggle button
 * <Button
 *   variant="toggle"
 *   size="custom"
 *   customSize="1rem"
 *   icon={<Circle />}
 *   tooltip="Set as default"
 *   active={isDefault}
 *   onClick={handleToggle}
 * />
 */
export const Button: React.FC<ButtonProps> = ({
    variant = 'neutral',
    size = 'medium',
    customSize,
    icon,
    tooltip,
    active = false,
    ...rest
  }) => {
  const theme = useTheme();

  // Size configurations
  const sizeStyles: Record<ButtonSize, SxProps<MuiTheme>> = {
    small: {
      width: '2rem',
      height: '2rem',
      '& .MuiSvgIcon-root': {
        fontSize: '1.25rem',
      },
    },
    medium: {
      width: '2.5rem',
      height: '2.5rem',
      '& .MuiSvgIcon-root': {
        fontSize: '1.5rem',
      },
    },
    large: {
      width: '3rem',
      height: '3rem',
      '& .MuiSvgIcon-root': {
        fontSize: '2rem',
      },
    },
    custom: customSize ? {
      width: customSize,
      height: customSize,
      '& .MuiSvgIcon-root': {
        fontSize: customSize,
      },
    } : {},
  };

  // Variant configurations
  const getVariantStyles = (): SxProps<MuiTheme> => {
    switch (variant) {
      case 'primary':
        return {
          backgroundColor: 'primary.main',
          color: 'white',
          '&:hover': {
            backgroundColor: 'primary.dark',
          },
          '&:disabled': {
            backgroundColor: 'primary.light',
            color: 'white',
            opacity: 0.6,
          },
        };

      case 'secondary':
        return {
          color: 'primary.main',
          backgroundColor: theme.palette.grey[200],
          '&:hover': {
            color: 'white',
            backgroundColor: 'primary.main',
          },
        };

      case 'error':
        return {
          color: theme.palette.grey[600],
          backgroundColor: theme.palette.grey[200],
          '&:hover': {
            color: 'error.dark',
            backgroundColor: theme.palette.grey[200],
          },
        };

      case 'neutral':
        return {
          color: theme.palette.grey[700],
          backgroundColor: theme.palette.grey[200],
          '&:hover': {
            backgroundColor: theme.palette.grey[300],
          },
        };

      case 'toggle':
        return {
          backgroundColor: active ? 'primary.main' : theme.palette.grey[300],
          color: active ? 'primary.main' : theme.palette.grey[300],
          '&:hover': {
            backgroundColor: active ? 'primary.dark' : theme.palette.grey[400],
            color: active ? 'primary.dark' : theme.palette.grey[400],
          },
        };

      default:
        return {};
    }
  };

  const buttonElement = (
    <IconButton
      {...rest}
      sx={[
        { flexShrink: 0 },
        sizeStyles[size],
        getVariantStyles(),
        ...(rest.sx ? [rest.sx] : []),
      ] as SxProps<MuiTheme>}
    >
      {icon}
    </IconButton>
  );

  // Wrap in Tooltip if tooltip text is provided
  // Wrapping in span to handle disabled state tooltip issue
  if (tooltip) {
    return (
      <Tooltip title={tooltip}>
        <span style={{ display: 'inline-flex' }}>{buttonElement}</span>
      </Tooltip>
    );
  }

  return buttonElement;
};

export default Button;